/**
 * ReferralTab — V2.9.5 — Friend invitations via Resend email.
 * Renders inside Account → Friends tab. User types a Gmail address, we send
 * an HTML invite. Hitting 10 unique invites grants the LE "Yea I Brought my Friends" badge.
 */
import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";

export default function ReferralTab() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [data, setData] = useState({ invites: [], unique_count: 0 });

  async function reload() {
    try { const { data: d } = await api.get("/referral/mine"); setData(d || { invites: [], unique_count: 0 }); }
    catch {}
  }
  useEffect(() => { reload(); }, []);

  async function send(e) {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data: r } = await api.post("/referral/invite", { friend_email: email });
      setMsg(`Invite sent! (${r.total_invites}/10 unique friends until the limited badge)`);
      setEmail("");
      await reload();
    } catch (ex) { setErr(fmtErr(ex.response?.data?.detail) || ex.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4" data-testid="referral-tab">
      <div className="bg-gradient-to-br from-amber-500/10 to-rose-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-base font-bold text-white"><i className="fa-solid fa-user-plus mr-2 text-amber-300"/>Refer a Friend</div>
          <div className="text-xs font-black text-amber-300">{data.unique_count}/10</div>
        </div>
        <p className="text-xs text-slate-400">Send a Gmail invite — when 10 of your friends sign up, you unlock the <b className="text-amber-300">"Yea I Brought my Friends"</b> limited-edition badge (first 1000 only).</p>
      </div>

      <form onSubmit={send} className="space-y-2">
        <input data-testid="referral-email" type="email" required value={email}
          onChange={e => setEmail(e.target.value.trim())} placeholder="friend@gmail.com"
          className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
        <button data-testid="referral-send" type="submit" disabled={busy || !email}
          className="w-full py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-900 rounded-lg text-xs font-black uppercase tracking-wider">
          {busy ? "Sending…" : <><i className="fa-solid fa-paper-plane mr-1.5"/>Send Invite</>}
        </button>
        {msg && <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded p-2">{msg}</div>}
        {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2" data-testid="referral-error">{err}</div>}
      </form>

      <div className="text-[10px] text-amber-200/70 bg-amber-500/5 border border-amber-500/20 rounded p-2">
        <i className="fa-solid fa-circle-info mr-1"/> Domain not verified at Resend? Invites currently only deliver to the verified support email. Verify your domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a> to enable invites to any Gmail.
      </div>

      {data.invites.length > 0 && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-3">
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Recent invites ({data.invites.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.invites.map(inv => (
              <div key={inv.id} className="flex items-center justify-between text-xs text-slate-300">
                <span>{inv.friend_email}</span>
                <span className="text-slate-600">{(inv.created_at || "").slice(0,10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
